<template>
  <div
    v-if="notifyWindow"
    class="fixed inset-0 z-(--z-modal) bg-backdrop/45 backdrop-blur-[2px] max-sm:hidden"
    @click="handleBackdropClick"
  >
    <div
      class="absolute inset-y-0 right-0 flex w-96 max-w-[92vw] flex-col gap-4 overflow-y-auto
        border-l border-line bg-bg2 p-6 shadow-[var(--shadow-modal)]"
      @mouseenter="store.dispatch('task/holdNotifyWindow')"
    >
      <template v-if="notifyWindow === 'choose_to_share'">
        <div class="flex w-full flex-1 flex-col items-center gap-4">
          <div :class="MAIN">{{ $t('trackingNotice.__tracking_going') }}</div>
          <div :class="SUB">{{ $t('trackingNotice.__please_share') }}</div>
          <Video
            :class="VIDEO"
            source="870:840"
            crop="844:764:16:8"
            :webmSrc="getLocalVideo('share_screen', 'share_screen', 'webm')"
            :mp4Src="getLocalVideo('share_screen', 'share_screen', 'mp4')"
          />
          <ul :class="BULLETS">
            <li :class="BULLET">{{ $t('trackingNotice.__tracking_makes_screenshots') }}</li>
            <li :class="BULLET">{{ $t('trackingNotice.__screenshots_securely') }}</li>
          </ul>
          <div :class="[FOOTNOTE, 'mt-auto']">{{ $t('trackingNotice.__press_cancel') }}</div>
        </div>
      </template>

      <template v-if="notifyWindow === 'retry_choose_to_share'">
        <div :class="MAIN">{{ $t('trackingNotice.__good_try') }}</div>
        <div :class="MAIN">
          {{ $t('trackingNotice.__selected_one') }}
          {{ metaInfoNotify }}
          {{ $t('trackingNotice.__selected_two') }}
        </div>
        <div :class="SUB">{{ $t('trackingNotice.__please_share') }}</div>
        <Video
          :class="VIDEO"
          source="870:840"
          crop="844:764:16:8"
          :webmSrc="getLocalVideo('share_screen', 'share_screen', 'webm')"
          :mp4Src="getLocalVideo('share_screen', 'share_screen', 'mp4')"
        />
        <Callout icon="secure">
          <p>{{ $t('trackingNotice.__tracking_going') }}</p>
          <p>{{ $t('trackingNotice.__screenshots_securely') }}</p>
        </Callout>
        <div :class="SUB">{{ $t('trackingNotice.__press_cancel') }}</div>
      </template>

      <template v-if="notifyWindow === 'cancel_provide_screen'">
        <div class="flex w-full flex-1 flex-col items-center gap-4">
          <div :class="MAIN">{{ $t('trackingNotice.__canceled_sharing') }}</div>
          <div :class="SUB">{{ $t('trackingNotice.__unfortunately') }}</div>
          <div :class="[BUTTONS, 'mt-2 w-full']">
            <SimpleButton
              class="w-full"
              variant="accent"
              :text="$t('trackingNotice.__try_again_btn')"
              @click="tryAgain"
            />
            <SimpleButton
              class="w-full"
              :text="$t('trackingNotice.__dont_track')"
              @click="disableTracking"
            />
          </div>
          <ul :class="BULLETS">
            <li :class="BULLET">{{ $t('trackingNotice.__choose_entire_screen') }}</li>
            <li :class="BULLET">{{ $t('trackingNotice.__window_or_tab_wont_work') }}</li>
          </ul>
          <div :class="[FOOTNOTE, 'mt-auto']" v-if="isWorkspaceAdmin">
            {{ $t('trackingNotice.__you_can_disable_screenshots') }}
            <router-link
              class="text-accent hover:underline"
              @click="disableTracking"
              :to="{ name: 'projectSettings', params: {
                projectSlug: project.slug,
                slugNameProject: project.nameSlug,
              } }"
            >{{ $t('trackingNotice.__project_settings') }}</router-link>
          </div>
        </div>
      </template>

      <template v-if="notifyWindow === 'request_notification'">
        <div :class="MAIN">{{ $t('trackingNotice.__we_will_notify') }}</div>
        <div :class="MAIN">{{ $t('trackingNotice.__please_notify') }}</div>
        <Video
          :class="VIDEO"
          source="654:400"
          crop="648:388:4:4"
          :webmSrc="getLocalVideo('show_notifications', 'show_notifications', 'webm', true)"
          :mp4Src="getLocalVideo('show_notifications', 'show_notifications', 'mp4', true)"
        />
        <Callout icon="warning" tone="warning">
          <p>{{ $t('trackingNotice.__notifications_only') }}</p>
          <p>{{ $t('trackingNotice.__we_recommended_notifications') }}</p>
        </Callout>
      </template>

      <template v-if="notifyWindow === 'request_idle'">
        <div :class="MAIN">{{ $t('trackingNotice.__we_recommended_idle') }}</div>
        <Video
          :class="VIDEO"
          source="654:400"
          crop="648:388:4:4"
          :webmSrc="getLocalVideo('actively_using', 'actively_using', 'webm', true)"
          :mp4Src="getLocalVideo('actively_using', 'actively_using', 'mp4', true)"
        />
        <Callout icon="warning" tone="warning">
          <p>{{ $t('trackingNotice.__needs_device_use') }}</p>
          <p>{{ $t('trackingNotice.__provide_better') }}</p>
        </Callout>
      </template>

      <template v-if="notifyWindow === 'track_started'">
        <div :class="MAIN">{{ $t('trackingNotice.__well_done') }}</div>
        <div :class="SUB">{{ $t('trackingNotice.__we_recommended_hide_popup') }}</div>
        <Video
          :class="VIDEO"
          source="850:200"
          crop="842:88:8:64"
          :webmSrc="getLocalVideo('stop_share', 'stop_share', 'webm')"
          :mp4Src="getLocalVideo('stop_share', 'stop_share', 'mp4')"
        />
        <div :class="SUB">{{ $t('trackingNotice.__to_leave') }}</div>
        <Checkbox
          v-model="dontShowAgain"
          class="flex-none! self-center text-body text-text [&_.label]:w-auto [&_.label]:flex-none"
        >
          {{ $t('trackingNotice.__dont_show_again') }}
        </Checkbox>
        <div :class="BUTTONS">
          <SimpleButton
            class="w-full"
            :text="$t('trackingNotice.__close')"
            @click="closeTrackStarted"
          />
        </div>
      </template>

      <template v-if="notifyWindow === 'reset_permission'">
        <div :class="MAIN">{{ $t('trackingNotice.__sorry_we_cant') }}</div>
        <div :class="MAIN">{{ $t('trackingNotice.__we_dont_have_perm') }}</div>
        <Video
          :class="VIDEO"
          source="654:800"
          crop="644:774:8:0"
          :webmSrc="getLocalVideo('reset_permissions', 'reset_permissions', 'webm')"
          :mp4Src="getLocalVideo('reset_permissions', 'reset_permissions', 'mp4')"
        />
        <div :class="SUB">{{ $t('trackingNotice.andReload') }}</div>
        <div :class="BUTTONS">
          <SimpleButton
            class="w-full"
            :text="$t('trackingNotice.__close')"
            @click="disableTracking"
          />
        </div>
      </template>

      <template v-if="notifyWindow === 'is_not_chrome_browser'">
        <div :class="MAIN" v-html="$t('trackingNotice.__choose_chrome_browser')"></div>
        <div :class="SUB">{{ $t('trackingNotice.__otherwise') }}</div>
        <div :class="BUTTONS">
          <SimpleButton
            class="w-full"
            variant="accent"
            :text="$t('trackingNotice.__download')"
            @click="openDownloadApp"
          />
          <SimpleButton
            class="w-full"
            :text="$t('trackingNotice.__close')"
            @click="disableTracking"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Video from '@/components/elements/Video';
import SimpleButton from '@/components/elements/SimpleButton';
import Callout from '@/components/elements/Callout';
import { noProposeTracking } from '@/utils/constantHolder';

const MAIN = 'text-group font-bold text-center text-text';
const SUB = 'text-body text-center text-text-muted';
const FOOTNOTE = 'text-label text-center text-text-muted';
const BULLETS = 'm-0 flex w-full list-none flex-col gap-2 p-0';
const BULLET = 'relative pl-4 text-label leading-snug text-text-muted '
  + 'before:absolute before:left-0 before:top-[0.4rem] before:size-1.5 before:rounded-full before:bg-accent';
const VIDEO = 'w-full overflow-hidden rounded-card';
const BUTTONS = 'flex flex-col gap-2.5';

const store = useStore();
const router = useRouter();

const play = ref(false);
const dontShowAgain = ref(false);

const notifyWindow = computed(() => store.state.task.notifyWindow);
const metaInfoNotify = computed(() => store.state.task.metaInfoNotify);
const project = computed(() => store.state.project.project);
const taskUnderEdit = computed(() => store.state.project.taskUnderEdit);
const workspace = computed(() => store.state.user.workspace);
const isWorkspaceAdmin = computed(() => store.getters['user/isWorkspaceAdmin']);

const actionChangeDoingScreenshot = (payload) => store.dispatch('project/actionChangeDoingScreenshot', payload);

const hideNotifyWindow = () => {
  store.commit('task/mutationNotifyWindow', null);
  store.dispatch('task/changeTitle', 'Tracklify.com');
};

const handleBackdropClick = (event) => {
  if (event.target === event.currentTarget) {
    hideNotifyWindow();
  }
};

const handleEscapeKey = (event) => {
  if (event.key === 'Escape' && notifyWindow.value) {
    event.preventDefault();
    event.stopPropagation();
    hideNotifyWindow();
  }
};

const getLocalVideo = (folder, nameVideo, extension, hasWinVersion) => {
  const locale = workspace.value?.locale;
  const platformPostfix = hasWinVersion && window.devicePlatform === 'win32' ? '_win' : '';
  const localePostfix = locale === 'en' ? '' : '_ru';
  return require(`@/assets/motion/${folder}/${nameVideo}${platformPostfix}${localePostfix}.${extension}`); // eslint-disable-line
};

const closeTrackStarted = () => {
  if (dontShowAgain.value) {
    localStorage.setItem('hide-share-window', true);
  }
  disableTracking();
};

const tryAgain = () => {
  store.dispatch('task/startTracking', { task: taskUnderEdit.value });
};

const openDownloadApp = () => {
  router.push({ name: 'getApp' });
  hideNotifyWindow();
};

const disableTracking = () => {
  hideNotifyWindow();
  localStorage.setItem(noProposeTracking, 'true');
};

onMounted(() => {
  document.addEventListener('keydown', handleEscapeKey, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEscapeKey, true);
});
</script>
